export interface MissionControlFailure {
  readonly category: 'http' | 'transport' | 'cancelled' | 'decode'
  readonly status?: number
  readonly code?: string
  readonly codeSource?: 'error_code' | 'code'
  readonly serviceCode?: string
  readonly connectCode?: string
  readonly contractMismatch?: boolean
  readonly requestId?: string
  readonly retryAfterMs?: number
  readonly limitedResource: 'api' | 'sandbox' | 'token_mint' | 'omitted' | 'unknown'
}

function own(value: unknown, key: string): unknown {
  if (!value || typeof value !== 'object') return undefined
  const descriptor = Object.getOwnPropertyDescriptor(value, key)
  return descriptor && 'value' in descriptor && descriptor.enumerable ? descriptor.value : undefined
}

export function missionControlCode(value: unknown): string | undefined {
  return typeof value === 'string' && /^[a-z][a-z0-9_]{0,127}$/.test(value) ? value : undefined
}

export function validatedMissionControlRequestId(value: unknown): string | undefined {
  return typeof value === 'string' && value.length <= 128 && /^[a-f0-9]{1,16}(?::[a-f0-9]{1,16}){4}$/i.test(value)
    ? value
    : undefined
}

export function parseMissionControlRetryAfter(value: string | null, now = Date.now()): number | undefined {
  if (value === null) return undefined
  const trimmed = value.trim()
  if (/^\d+$/.test(trimmed)) {
    const ms = Number(trimmed) * 1_000
    return Number.isSafeInteger(ms) ? ms : undefined
  }
  let httpDate = trimmed
  const obsolete =
    /^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday), (\d{2})-([A-Z][a-z]{2})-(\d{2}) (\d{2}:\d{2}:\d{2}) GMT$/.exec(
      trimmed,
    )
  if (obsolete) {
    const currentYear = new Date(now).getUTCFullYear()
    let year = Math.floor(currentYear / 100) * 100 + Number(obsolete[4])
    const candidate = Date.parse(`${obsolete[2]} ${obsolete[3]} ${year} ${obsolete[5]} GMT`)
    const futureLimit = new Date(now)
    futureLimit.setUTCFullYear(currentYear + 50)
    if (candidate > futureLimit.getTime()) year -= 100
    httpDate = `${obsolete[2]} ${obsolete[3]} ${year} ${obsolete[5]} GMT`
  } else if (/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun) [A-Z][a-z]{2} [ \d]\d \d{2}:\d{2}:\d{2} \d{4}$/.test(trimmed)) {
    httpDate = `${trimmed} GMT`
  } else if (
    !/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun), \d{2} (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4} \d{2}:\d{2}:\d{2} GMT$/.test(
      trimmed,
    )
  ) {
    return undefined
  }
  const date = Date.parse(httpDate)
  return Number.isFinite(date) ? Math.max(0, date - now) : undefined
}

export function decodeMissionControlFailure(
  status: number,
  body: unknown,
  headers?: Headers,
  connect = false,
): MissionControlFailure {
  const serviceCode = missionControlCode(own(body, 'error_code'))
  const connectCode = missionControlCode(own(body, 'code'))
  const code = connect ? (connectCode ?? serviceCode) : (serviceCode ?? connectCode)
  const codeSource =
    code === undefined ? undefined : connect && connectCode ? 'code' : serviceCode ? 'error_code' : 'code'
  const resource = own(body, 'limited_resource')
  const limitedResource =
    resource === undefined
      ? 'omitted'
      : resource === 'api' || resource === 'sandbox' || resource === 'token_mint'
        ? resource
        : 'unknown'
  const requestId = validatedMissionControlRequestId(headers?.get('X-GitHub-Request-ID'))
  const retryAfterMs = parseMissionControlRetryAfter(headers?.get('Retry-After') ?? null)
  return Object.freeze({
    category: 'http',
    status,
    ...(code ? {code, codeSource} : {}),
    ...(serviceCode ? {serviceCode} : {}),
    ...(connectCode ? {connectCode} : {}),
    ...(serviceCode && connectCode && serviceCode !== connectCode ? {contractMismatch: true} : {}),
    ...(requestId ? {requestId} : {}),
    ...(retryAfterMs !== undefined ? {retryAfterMs} : {}),
    limitedResource,
  })
}

/** Read only approved own data properties; never traverse an Error, cause, or response body. */
export function missionControlFailureFromUnknown(value: unknown): MissionControlFailure | undefined {
  const category = own(value, 'category')
  const status = own(value, 'status')
  const code = own(value, 'code')
  const codeSource = own(value, 'codeSource')
  const serviceCode = own(value, 'serviceCode')
  const connectCode = own(value, 'connectCode')
  const contractMismatch = own(value, 'contractMismatch')
  const requestId = own(value, 'requestId')
  const retryAfterMs = own(value, 'retryAfterMs')
  const limitedResource = own(value, 'limitedResource')
  if (
    (category !== 'http' && category !== 'transport' && category !== 'cancelled' && category !== 'decode') ||
    (status !== undefined &&
      (typeof status !== 'number' || !Number.isInteger(status) || status < 100 || status > 599)) ||
    (code !== undefined && missionControlCode(code) === undefined) ||
    (serviceCode !== undefined && missionControlCode(serviceCode) === undefined) ||
    (connectCode !== undefined && missionControlCode(connectCode) === undefined) ||
    (codeSource !== undefined && codeSource !== 'error_code' && codeSource !== 'code') ||
    (contractMismatch !== undefined && typeof contractMismatch !== 'boolean') ||
    (requestId !== undefined && validatedMissionControlRequestId(requestId) === undefined) ||
    (retryAfterMs !== undefined &&
      (typeof retryAfterMs !== 'number' || !Number.isSafeInteger(retryAfterMs) || retryAfterMs < 0)) ||
    (limitedResource !== 'api' &&
      limitedResource !== 'sandbox' &&
      limitedResource !== 'token_mint' &&
      limitedResource !== 'omitted' &&
      limitedResource !== 'unknown')
  ) {
    return undefined
  }

  return Object.freeze({
    category,
    ...(typeof status === 'number' ? {status} : {}),
    ...(typeof code === 'string' ? {code} : {}),
    ...(codeSource ? {codeSource} : {}),
    ...(typeof serviceCode === 'string' ? {serviceCode} : {}),
    ...(typeof connectCode === 'string' ? {connectCode} : {}),
    ...(contractMismatch !== undefined ? {contractMismatch} : {}),
    ...(typeof requestId === 'string' ? {requestId} : {}),
    ...(typeof retryAfterMs === 'number' ? {retryAfterMs} : {}),
    limitedResource,
  })
}

export function missionControlFailureFromError(error: unknown): MissionControlFailure | undefined {
  return (
    missionControlFailureFromUnknown(own(error, 'mcFailure')) ??
    missionControlFailureFromUnknown(own(own(error, 'failure'), 'mcFailure'))
  )
}

export function abortableMissionControl<T>(work: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return work
  if (signal.aborted) {
    // eslint-disable-next-line github/no-then -- Observe late rejection without delaying cancellation.
    void work.catch(() => {})
    return Promise.reject(signal.reason)
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(signal.reason)
    signal.addEventListener('abort', onAbort, {once: true})
    void (async () => {
      try {
        resolve(await work)
      } catch (error) {
        reject(error)
      } finally {
        signal.removeEventListener('abort', onAbort)
      }
    })()
  })
}
